import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp, Download, Copy, Check } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface SpreadsheetTableProps {
  data?: {
    headers: string[];
    rows: (string | number)[][];
  };
  title?: string;
  className?: string;
  onSort?: (columnIndex: number, direction: 'asc' | 'desc') => void;
}

const SpreadsheetTable: React.FC<SpreadsheetTableProps> = ({
  data = { headers: [], rows: [] },
  title,
  className = '',
  onSort,
}) => {
  const [sortConfig, setSortConfig] = useState<{ column: number; direction: 'asc' | 'desc' } | null>(null);
  const [copied, setCopied] = useState(false);

  const formatCell = (value: string | number): string => {
    if (typeof value === 'number') {
      if (value >= 0 && value <= 1) {
        return `${(value * 100).toFixed(1)}%`;
      }
      if (Math.abs(value) >= 1000) {
        return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
      }
      return value.toLocaleString();
    }
    return value;
  };

  const handleSort = (columnIndex: number) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.column === columnIndex && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ column: columnIndex, direction });
    if (onSort) onSort(columnIndex, direction);
  };

  const sortedRows = useMemo(() => {
    if (onSort || !sortConfig) return data.rows;
    return [...data.rows].sort((a, b) => {
      const aValue = a[sortConfig.column];
      const bValue = b[sortConfig.column];
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      return sortConfig.direction === 'asc'
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [data.rows, sortConfig, onSort]);

  return (
    <div className={`bg-white/95 backdrop-blur-sm rounded-xl shadow-xl overflow-hidden border border-gray-200/50 ${className}`}>
      {data.headers.length > 0 && (
        <table className="w-full text-sm text-left">
          <thead>
            <tr>
              {data.headers.map((header, index) => (
                <th key={index} onClick={() => handleSort(index)}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex}>{formatCell(cell)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default SpreadsheetTable;
